import {
	Box,
	Icons,
	Inline,
	Money,
	Stack,
	Table,
	TableDataCell,
	TableHeaderDataCell,
	Text,
} from "@sembark-travel/ui/base"
import { generatePath, useNavigate } from "../../router-utils"
import {
	CompareSheetFileVersions,
	ReadSheetFileVersionDetails,
} from "../Sheets"
import { IAddCabMetricPrices } from "./NewItem"
import {
	cabPriceCalculationMetricPricesSheetsXHR,
	TCabMetricSheetFileData,
} from "./store"
import {
	findDiffBetweenCabMetricPrices,
	transformMetricSheetFileDataToFormValues,
} from "./utils"
import { formatDate } from "@sembark-travel/datetime-utils"
import { Fragment, useMemo } from "react"
import { ShowCabPriceCalculationMetricDetails } from "../../CabPriceCalculationMetrics"
import { ShowCabCapacityInfo } from "../../CabTypes"

export function CabMetricPricesFileVersionDetails({
	folderId,
	fileId,
	versionId,
}: {
	folderId: string
	fileId: string
	versionId: string
}) {
	return (
		<ReadSheetFileVersionDetails<TCabMetricSheetFileData, IAddCabMetricPrices>
			sheetFolderId={folderId}
			sheetId={fileId}
			sheetVersionId={versionId}
			getLinkToEdit={(folderId, fileId, versionId) => {
				return generatePath(
					"/data-drive/folders/:folderId/files/:fileId/versions/:versionId/edit/metric",
					{
						folderId: folderId,
						fileId: fileId,
						versionId: versionId,
					}
				)
			}}
			service={{
				fetchSheetVersionDetails: (xhr, versionId) => {
					return cabPriceCalculationMetricPricesSheetsXHR(
						xhr
					).getCabMetricSheetFileVersionDetails(versionId)
				},
			}}
			transformDataToFormValues={transformMetricSheetFileDataToFormValues}
		>
			{({ sheetValues }) =>
				sheetValues ? (
					<ReadCabMetricPriceSpreadSheet cabMetricSheetValues={sheetValues} />
				) : null
			}
		</ReadSheetFileVersionDetails>
	)
}

export function ReadCabMetricPriceSpreadSheet({
	cabMetricSheetValues,
}: {
	cabMetricSheetValues: IAddCabMetricPrices
}) {
	return (
		<Box padding={"2"}>
			<Stack
				overflowX="auto"
				position={"relative"}
				style={{
					height: "calc(100vh - 200px)",
				}}
				gap="2"
			>
				<Box
					backgroundColor={"default"}
					padding={"2"}
					borderWidth={"1"}
					borderColor={"muted"}
					style={{
						maxWidth: "140px",
					}}
				>
					<Text fontSize={"sm"}>
						Currency: {cabMetricSheetValues.config.currency || "---"}
					</Text>
				</Box>

				<Box
					position={"relative"}
					overflowX="auto"
					overflowY="auto"
					zIndex="0"
					style={{
						maxHeight: "calc(100vh - 250px)",
					}}
					flexGrow={`1`}
				>
					<Table
						width="full"
						position={"relative"}
						zIndex="0"
						style={{
							borderSpacing: "0 6px",
						}}
					>
						<thead>
							<tr
								style={{
									position: "sticky",
									top: 0,
									zIndex: 8,
								}}
							>
								<TableHeaderDataCell
									position={"sticky"}
									left={"0"}
									backgroundColor={"default"}
									boxShadow={"sm"}
									style={{
										minWidth: "200px",
										zIndex: 4,
									}}
									borderWidth={"1"}
									padding={"2"}
									fontSize="sm"
									fontWeight="semibold"
								>
									Cab Type
								</TableHeaderDataCell>
								<TableHeaderDataCell
									position={"sticky"}
									backgroundColor={"default"}
									boxShadow={"sm"}
									style={{
										minWidth: "200px",
										left: "200px",
										zIndex: 4,
									}}
									borderWidth={"1"}
									padding={"2"}
									fontSize="sm"
									fontWeight="semibold"
								>
									Calculation Metric
								</TableHeaderDataCell>
								{(cabMetricSheetValues.seasons || []).map(
									(season, seasonIndex) => (
										<TableHeaderDataCell
											key={`season[${seasonIndex}]`}
											borderWidth="1"
											backgroundColor={"default"}
											padding="2"
											style={{
												minWidth: "250px",
											}}
											fontSize="sm"
											fontWeight="semibold"
										>
											{season.name}
										</TableHeaderDataCell>
									)
								)}
							</tr>

							<tr>
								<TableHeaderDataCell
									borderWidth={"1"}
									padding={"2"}
									colSpan={2}
									position={"sticky"}
									left={"0"}
									backgroundColor={"default"}
									boxShadow={"sm"}
									style={{
										zIndex: 4,
									}}
								></TableHeaderDataCell>

								{(cabMetricSheetValues.seasons || []).map(
									(season, seasonIndex) => (
										<TableHeaderDataCell
											key={`${seasonIndex}-dates`}
											borderWidth="1"
											padding="2"
											maxWidth="sm"
											verticalAlign="top"
										>
											<Stack gap="2" justifyContent="start">
												{(season.date_ranges || []).map((range, index) => (
													<Inline
														key={`season[${seasonIndex}].date-range[${index}]`}
														padding={"2"}
														borderWidth={"1"}
														borderColor={"muted"}
													>
														<Text fontSize={"sm"}>
															{range.start_date
																? formatDate(range.start_date, "DD MMM YYYY")
																: "---"}{" "}
															-{" "}
															{range.end_date
																? formatDate(range.end_date, "DD MMM YYYY")
																: "---"}
														</Text>
													</Inline>
												))}
											</Stack>
										</TableHeaderDataCell>
									)
								)}
							</tr>
						</thead>

						<tbody>
							{(cabMetricSheetValues.cab_prices || []).map(
								(cabPrice, cabPriceIndex) => {
									return (cabPrice.cab_price_calculation_metrics || []).map(
										(metric, metricIndex) => (
											<tr
												key={`cab-price-row[${cabPriceIndex}].metric[${metricIndex}]`}
											>
												<TableDataCell
													position={"sticky"}
													left={"0"}
													backgroundColor={"default"}
													style={{
														minWidth: "200px",
														zIndex: -cabPriceIndex * 50 - metricIndex,
													}}
													borderWidth={"1"}
													borderColor={"muted"}
													paddingX={"2"}
													paddingY={"2"}
												>
													{metricIndex === 0 ? (
														<>
															<Text>{cabPrice.cab_type?.name || "---"}</Text>
															{cabPrice.cab_type ? (
																<ShowCabCapacityInfo
																	cabType={cabPrice.cab_type}
																/>
															) : null}
														</>
													) : null}
												</TableDataCell>

												<TableDataCell
													position={"sticky"}
													backgroundColor={"default"}
													boxShadow={"sm"}
													style={{
														minWidth: "200px",
														left: "200px",
														zIndex: -cabPriceIndex * 50 - metricIndex,
													}}
													borderWidth={"1"}
													borderColor={"muted"}
													paddingX={"2"}
													paddingY={"2"}
												>
													<Inline gap={"1"} alignItems={"center"}>
														<Text>{metric.metric?.name || "---"}</Text>
														{metric.metric?.deleted_at ? (
															<Icons.Info color="danger" title="Archive" />
														) : null}
													</Inline>
													{metric.metric?.system_quantity_metric ||
													metric.metric?.min_quantity_per_day ? (
														<ShowCabPriceCalculationMetricDetails
															metric={metric.metric}
														/>
													) : null}
												</TableDataCell>

												{(cabMetricSheetValues.seasons || []).map(
													(season, seasonIndex) => (
														<TableDataCell
															key={`cab-price[${cabPriceIndex}].metric[${metricIndex}].season_price[${seasonIndex}].price`}
															position={"relative"}
															style={{
																zIndex:
																	-cabPriceIndex * 50 -
																	metricIndex -
																	seasonIndex -
																	1,
															}}
															borderWidth={"1"}
															borderColor={"muted"}
															paddingX={"2"}
															paddingY={"2"}
														>
															{metric?.season_prices?.[season.id]?.price ? (
																<Money
																	amount={
																		metric.season_prices[season.id].price || 0
																	}
																	currency={
																		cabMetricSheetValues.config.currency
																	}
																/>
															) : (
																"---"
															)}
														</TableDataCell>
													)
												)}
											</tr>
										)
									)
								}
							)}
						</tbody>
					</Table>
				</Box>
			</Stack>
		</Box>
	)
}

export function CompareCabMetricsPricesSheet({
	metricSheetId,
	metricSheetVersionId1,
	metricSheetVersionId2,
}: {
	metricSheetId: string
	metricSheetVersionId1: string
	metricSheetVersionId2: string
}) {
	const navigate = useNavigate()

	return (
		<Stack padding={"4"}>
			<CompareSheetFileVersions<TCabMetricSheetFileData, IAddCabMetricPrices>
				sheetId={metricSheetId}
				sheetVersionId1={metricSheetVersionId1}
				sheetVersionId2={metricSheetVersionId2}
				xhrFetchBaseUrl="/data-drive"
				service={{
					fetchSheetDetails: (xhr, fileId) => {
						return cabPriceCalculationMetricPricesSheetsXHR(
							xhr
						).getCabMetricSheetFileDetails(fileId)
					},
					fetchSheetVersionDetails: (xhr, versionId) => {
						return cabPriceCalculationMetricPricesSheetsXHR(
							xhr
						).getCabMetricSheetFileVersionDetails(versionId)
					},
				}}
				transformDataToFormValues={transformMetricSheetFileDataToFormValues}
				onCompareWithSheetChange={(
					folderId,
					folderName,
					fileId,
					fileName,
					versionId1,
					versionId2
				) => {
					navigate(
						{
							pathname:
								"/data-drive/folders/:folderId/files/:fileId/compare/:versionId1/:versionId2/metric",
							search: `?folder=${folderName || ""}&file=${fileName || ""}`,
						},
						{
							params: {
								folderId: folderId,
								fileId: fileId,
								versionId1: versionId1,
								versionId2: versionId2,
							},
						}
					)
				}}
			>
				{({ sheet1Values, sheet2Values }) =>
					sheet1Values && sheet2Values ? (
						<CompareCabMetricsPricesSheetsWithDiff
							metricSheet1Values={sheet1Values}
							metricSheet2Values={sheet2Values}
						/>
					) : null
				}
			</CompareSheetFileVersions>
		</Stack>
	)
}

export function CompareCabMetricsPricesSheetsWithDiff({
	metricSheet1Values,
	metricSheet2Values,
}: {
	metricSheet1Values: IAddCabMetricPrices
	metricSheet2Values: IAddCabMetricPrices
}) {
	const cabPriceDiff: {
		original: (Partial<IAddCabMetricPrices["cab_prices"][number]> & {
			__comparison: string
			row_space?: number
		})[]
		modified: (Partial<IAddCabMetricPrices["cab_prices"][number]> & {
			__comparison: string
			row_space?: number
		})[]
	} | null = useMemo(() => {
		if (metricSheet1Values && metricSheet2Values) {
			return findDiffBetweenCabMetricPrices(
				metricSheet1Values?.cab_prices,
				metricSheet2Values?.cab_prices
			)
		}

		return null
	}, [metricSheet1Values, metricSheet2Values])

	return (
		<Inline alignItems="start" gap={"1"} width={"full"}>
			<Box width="1/2">
				<ReadOnlyCabMetricPriceSpreadSheetWithComparison
					cabMetricSheetValues={{
						...metricSheet1Values,
						cab_prices:
							cabPriceDiff?.original as IAddCabMetricPrices["cab_prices"],
					}}
					comparisonData={{
						...metricSheet2Values,
						cab_prices:
							cabPriceDiff?.modified as IAddCabMetricPrices["cab_prices"],
					}}
					isGettingCompared={false}
				/>
			</Box>
			<Box width="1/2">
				<ReadOnlyCabMetricPriceSpreadSheetWithComparison
					cabMetricSheetValues={{
						...metricSheet2Values,
						cab_prices:
							cabPriceDiff?.modified as IAddCabMetricPrices["cab_prices"],
					}}
					comparisonData={{
						...metricSheet1Values,
						cab_prices:
							cabPriceDiff?.original as IAddCabMetricPrices["cab_prices"],
					}}
					isGettingCompared={true}
				/>
			</Box>
		</Inline>
	)
}

export function ReadOnlyCabMetricPriceSpreadSheetWithComparison({
	cabMetricSheetValues,
	isGettingCompared = false,
	comparisonData,
}: {
	cabMetricSheetValues: IAddCabMetricPrices
	comparisonData: IAddCabMetricPrices
	isGettingCompared: boolean
}) {
	const getComparisonData = (
		originalValue: unknown,
		currentValue: unknown
	): boolean => {
		let isDifferent = false
		if (originalValue && currentValue) {
			isDifferent =
				JSON.stringify(originalValue) !== JSON.stringify(currentValue)
		}

		return isDifferent
	}

	return (
		<Box padding={"2"}>
			<Stack
				overflowX="auto"
				position={"relative"}
				style={{
					height: "calc(100vh - 200px)",
				}}
				gap="2"
			>
				{
					<Box
						backgroundColor={"default"}
						padding={"2"}
						borderWidth={"1"}
						borderColor={"muted"}
						style={{
							maxWidth: "140px",
						}}
					>
						<Text fontSize={"sm"}>
							Currency: {cabMetricSheetValues.config.currency || "---"}
						</Text>
					</Box>
				}

				<Box
					position={"relative"}
					overflowX="auto"
					overflowY="auto"
					zIndex="0"
					style={{
						maxHeight: "calc(100vh - 250px)",
					}}
					flexGrow={`1`}
				>
					<Table
						width="full"
						position={"relative"}
						zIndex="0"
						style={{
							borderSpacing: "0 6px",
						}}
					>
						<thead>
							<tr
								style={{
									position: "sticky",
									top: 0,
									zIndex: 8,
								}}
							>
								<TableHeaderDataCell
									position={"sticky"}
									left={"0"}
									backgroundColor={"default"}
									boxShadow={"sm"}
									style={{
										minWidth: "200px",
										zIndex: 4,
									}}
									borderWidth={"1"}
									padding={"2"}
									fontSize={"sm"}
									fontWeight="semibold"
								>
									Cab Type
								</TableHeaderDataCell>
								<TableHeaderDataCell
									position={"sticky"}
									backgroundColor={"default"}
									boxShadow={"sm"}
									style={{
										minWidth: "200px",
										left: "200px",
										zIndex: 4,
									}}
									borderWidth={"1"}
									padding={"2"}
									fontSize={"sm"}
									fontWeight="semibold"
								>
									Calculation Metric
								</TableHeaderDataCell>
								{(cabMetricSheetValues.seasons || []).map(
									(season, seasonIndex) => {
										const isDiff = getComparisonData(
											comparisonData?.seasons?.[seasonIndex]?.name,
											season.name
										)

										return (
											<TableHeaderDataCell
												key={season.id}
												borderWidth="1"
												padding="2"
												style={{
													minWidth: "250px",
												}}
												bgColor={isDiff ? "danger" : undefined}
												fontSize="sm"
												fontWeight="semibold"
												backgroundColor={"default"}
											>
												{season.name}
											</TableHeaderDataCell>
										)
									}
								)}
							</tr>

							<tr>
								<TableHeaderDataCell
									borderWidth={"1"}
									padding={"2"}
									colSpan={2}
									position={"sticky"}
									left={"0"}
									backgroundColor={"default"}
									boxShadow={"sm"}
									style={{
										zIndex: 4,
									}}
								></TableHeaderDataCell>

								{(cabMetricSheetValues.seasons || []).map(
									(season, seasonIndex) => {
										return (
											<TableHeaderDataCell
												key={`${seasonIndex}-dates`}
												borderWidth="1"
												padding="2"
												maxWidth="sm"
												verticalAlign="top"
											>
												<Stack gap="2" justifyContent="start">
													{(season.date_ranges || []).map((range, index) => {
														const comparisonDateRange =
															comparisonData.seasons?.[seasonIndex]
																?.date_ranges?.[index]

														const isStartDateDiff = getComparisonData(
															comparisonDateRange?.start_date,
															range.start_date
														)
														const isEndDateDiff = getComparisonData(
															comparisonDateRange?.end_date,
															range.end_date
														)

														return (
															<Inline
																key={`season[${seasonIndex}].date-range[${index}]`}
																padding={"2"}
																borderWidth={"1"}
																borderColor={"muted"}
																bgColor={
																	isStartDateDiff || isEndDateDiff
																		? "danger"
																		: undefined
																}
															>
																<Text fontSize={"sm"}>
																	{range.start_date
																		? formatDate(
																				range.start_date,
																				"DD MMM YYYY"
																			)
																		: "---"}{" "}
																	-{" "}
																	{range.end_date
																		? formatDate(range.end_date, "DD MMM YYYY")
																		: "---"}
																</Text>
															</Inline>
														)
													})}
												</Stack>
											</TableHeaderDataCell>
										)
									}
								)}
							</tr>
						</thead>

						<tbody>
							{(cabMetricSheetValues.cab_prices || []).map(
								(cabPrice, cabPriceIndex) => {
									const currentRow = cabPrice as Partial<
										IAddCabMetricPrices["cab_prices"][number]
									> & { __comparison: string; row_space?: number }

									const comparisonFlag = currentRow?.__comparison || null
									const rowSpace = currentRow?.row_space || 1

									let removed = false
									let added = false
									let changed = false

									if (comparisonFlag === "removed") removed = true
									if (comparisonFlag === "added") added = true
									if (comparisonFlag === "changed") changed = true

									if (!isGettingCompared && comparisonFlag === "added") {
										return (
											<Fragment key={`added-row-${cabPriceIndex}`}>
												{Array.from({ length: rowSpace || 1 }).map(
													(_, index) => (
														<tr key={index}>
															<TableDataCell
																style={{
																	width: "250px",
																	height: "38px",
																}}
																colSpan={2}
																backgroundColor={
																	removed
																		? "danger"
																		: added
																			? "success"
																			: changed
																				? "warning"
																				: "default"
																}
																paddingX={"2"}
																paddingY={"2"}
															>
																&nbsp;
															</TableDataCell>
														</tr>
													)
												)}
											</Fragment>
										)
									}

									if (isGettingCompared && comparisonFlag === "removed") {
										return (
											<Fragment key={`removed-row-${cabPriceIndex}`}>
												{Array.from({ length: rowSpace || 1 }).map(
													(_, index) => (
														<tr key={index}>
															<TableDataCell
																style={{
																	width: "250px",
																	height: "38px",
																}}
																paddingX={"2"}
																paddingY={"2"}
																colSpan={2}
																backgroundColor={
																	removed
																		? "danger"
																		: added
																			? "success"
																			: changed
																				? "warning"
																				: "default"
																}
															>
																&nbsp;
															</TableDataCell>
														</tr>
													)
												)}
											</Fragment>
										)
									}

									const isCabTypeDiff = getComparisonData(
										comparisonData?.cab_prices?.[cabPriceIndex]?.cab_type?.name,
										cabPrice.cab_type?.name
									)

									return (cabPrice.cab_price_calculation_metrics || []).map(
										(metric, metricIndex) => {
											const isMetricDiff = getComparisonData(
												comparisonData.cab_prices?.[cabPriceIndex]
													?.cab_price_calculation_metrics?.[metricIndex]?.metric
													?.name,
												metric.metric?.name
											)

											return (
												<tr
													key={`cab-price-row[${cabPriceIndex}].metric[${metricIndex}]`}
												>
													{
														<TableDataCell
															position={"sticky"}
															left={"0"}
															backgroundColor={
																removed || isCabTypeDiff
																	? "danger"
																	: added
																		? "success"
																		: changed
																			? "warning"
																			: "default"
															}
															style={{
																minWidth: "200px",
																zIndex: -cabPriceIndex * 50 - metricIndex,
															}}
															paddingX={"2"}
															paddingY={"2"}
															borderWidth={"1"}
															borderColor={"muted"}
														>
															{metricIndex === 0 ? (
																<Text>{cabPrice.cab_type?.name || "---"}</Text>
															) : null}
														</TableDataCell>
													}

													<TableDataCell
														position={"sticky"}
														boxShadow={"sm"}
														style={{
															minWidth: "200px",
															left: "200px",
															zIndex: -cabPriceIndex * 50 - metricIndex,
														}}
														backgroundColor={
															removed || isMetricDiff
																? "danger"
																: added
																	? "success"
																	: changed
																		? "warning"
																		: "default"
														}
														paddingX={"2"}
														paddingY={"2"}
														borderWidth={"1"}
														borderColor={"muted"}
													>
														<Text>{metric.metric?.name || "---"}</Text>
													</TableDataCell>

													{(cabMetricSheetValues.seasons || []).map(
														(season, seasonIndex) => {
															const compareSeason =
																comparisonData?.seasons?.[seasonIndex]

															const comparisonCabPrice =
																comparisonData.cab_prices?.[cabPriceIndex]
																	?.cab_price_calculation_metrics?.[metricIndex]
																	?.season_prices?.[compareSeason?.id || ""]
																	?.price

															const isMetricSeasonPriceDiff = getComparisonData(
																comparisonCabPrice,
																metric?.season_prices?.[season.id]?.price
															)

															return (
																<TableDataCell
																	position={"relative"}
																	key={`cab-price[${cabPriceIndex}].metric[${metricIndex}].season_price[${seasonIndex}].price`}
																	style={{
																		zIndex:
																			-cabPriceIndex * 50 -
																			metricIndex -
																			seasonIndex -
																			1,
																	}}
																	backgroundColor={
																		removed || isMetricSeasonPriceDiff
																			? "danger"
																			: added
																				? "success"
																				: changed
																					? "warning"
																					: "default"
																	}
																	borderWidth={"1"}
																	borderColor={"muted"}
																	paddingX={"2"}
																	paddingY={"2"}
																>
																	{metric?.season_prices?.[season.id]?.price ? (
																		<Money
																			amount={
																				metric.season_prices[season.id].price ||
																				0
																			}
																			currency={
																				cabMetricSheetValues.config.currency
																			}
																		/>
																	) : (
																		"---"
																	)}
																</TableDataCell>
															)
														}
													)}
												</tr>
											)
										}
									)
								}
							)}
						</tbody>
					</Table>
				</Box>
			</Stack>
		</Box>
	)
}
